import { CloudDownloadOutlined, EyeOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import {
  setAnalysisImageId,
  setAnalysisWorkId
} from '@/features/SetAnalysisImageInfo/setAnalysisImageInfo'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { getImagesOfWorkRequest, getLikesList, getPaintingIntro, SRC_URL } from '@/server'
import * as d3 from 'd3'
import './index.less'
import { SelectWorkList } from '@/components/SelectWorkList'
import {
  setIdList,
  setLikesNowPage,
  setLikesShowList,
  setLikesTotal,
  showListI
} from '@/features/SetLikesPageList/setLikesPageList'
import { setToken } from '@/features/Login/login'
import { useNavigate } from 'react-router-dom'

interface LikesPageProps {
  setComponent: Function
}
export function LikesPage(props: LikesPageProps) {
  const { setComponent } = props
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const token = useAppSelector((state) => state.login.token)
  // 总页数
  const pageTotal = useAppSelector((state) => state.likesPageList.pageTotal)
  // 当前页数
  const nowPage = useAppSelector((state) => state.likesPageList.nowPage)
  // 一页容量
  const size = 10
  // 分页器状态
  const [pageState, setPageState] = useState<any>([])
  // 收藏页的workid列表
  const idList = useAppSelector((state) => state.likesPageList.idList)
  // 当前展示的list
  const showList = useAppSelector((state) => state.likesPageList.showList)
  // 待选列表的list
  const [selectList, setSelectList] = useState<{ id: string; imageId: string; src: string }[]>([])
  // 每张图的三角形icon是否展示
  const [iconArr, setIconArr] = useState<boolean[]>(Array(size).fill(false))

  // 切换页面
  const handleChangePage = function (pageindex: number) {
    if (pageindex < 1 || pageindex > pageTotal) return
    dispatch(setLikesNowPage(pageindex))
    getLikesList(token, { page: pageindex, size: size }).then((res) => {
      if (res.data.code === '10056') {
        //token失效
        window.confirm('登录已过期，请重新登录！')
        dispatch(setToken(''))
        navigate('/')
      } else {
        // 获取该用户的收藏页数
        dispatch(setLikesTotal(Math.ceil(res.data.content.total / size)))
        // 更新idList
        dispatch(setIdList(res.data.content.list))
      }
    })
  }
  // 画作列表中，画作鼠标点击事件
  const handleSelectItem = function (id: string, index: number) {
    getImagesOfWorkRequest({ workId: id }, token).then((res) => {
      const list = res.data.content.map((work) => {
        return {
          imageId: work.imageid,
          src: work.fullpath
        }
      })
      if (list.length === 1) {
        dispatch(setAnalysisWorkId(id))
        dispatch(setAnalysisImageId(list[0].imageId))
        setComponent(2)
      } else {
        getImagesOfWorkRequest({ workId: id }, token).then((res) => {
          const list = res.data.content.map((work) => {
            return {
              id,
              imageId: work.imageid,
              src: work.fullpath
            }
          })
          setSelectList(list)
          // 需要展示待选列表
          if (list.length > 1) {
            const selectWorkListDiv = d3.select('#select-work-list')
            const itemHeight = d3.select('.likes-page-item')._groups[0][0].clientHeight

            // 设置待选列表展示的位置，index < 5则显示在下方，index>=5则显示在上方
            if (index < size / 2)
              selectWorkListDiv.style('top', '').style('bottom', (1.8 * itemHeight) / 3 + 'px')
            else selectWorkListDiv.style('top', itemHeight / 2.5 + 'px').style('bottom', '')
            // 设置三角形icon的显隐
            setIconArr(
              iconArr.map((_icon, i) => {
                if (i !== index) return false
                else return true
              })
            )
          } else {
            setIconArr(Array(size).fill(false))
          }
        })
      }
    })
  }
  // idList更新,重新请求画作
  useEffect(() => {
    //promise数组
    const promiseList: Promise<any>[] = []
    for (let i = 0; i < idList.length; i++)
      promiseList.push(getPaintingIntro(token, { workId: idList[i] }))
    Promise.all(promiseList).then((values) => {
      const list: showListI[] = []
      for (let i = 0; i < values.length; i++) {
        list.push({
          workId: values[i].data.content.workid,
          workName: values[i].data.content.workname,
          authorName: values[i].data.content.authorname,
          dynasty: values[i].data.content.dynasty,
          museum: values[i].data.content.instname,
          src: values[i].data.content.encoding
        })
      }
      dispatch(setLikesShowList(list))
    })
  }, [idList])
  // 更新分页器状态
  useEffect(() => {
    // 阈值
    if (pageTotal < 22) {
      setPageState(
        Array(pageTotal)
          .fill(0)
          .map((_item, i) => i + 1)
      )
    } else {
      if (nowPage <= 3) {
        setPageState([1, 2, 3, 4, 'R', pageTotal])
      } else if (nowPage >= pageTotal - 2) {
        setPageState([1, 'L', pageTotal - 3, pageTotal - 2, pageTotal - 1, pageTotal])
      } else {
        setPageState([1, 'L', nowPage - 1, nowPage, nowPage + 1, 'R', pageTotal])
      }
    }
  }, [pageTotal, nowPage])
  // 点击待选列表外部，清除待选列表
  useEffect(() => {
    const func = (e: any) => {
      const selectWorkListDiv = document.querySelector('#select-work-list')
      if (selectWorkListDiv && !selectWorkListDiv.contains(e.target)) {
        setSelectList([])
        setIconArr(Array(size).fill(false))
      }
    }
    document.addEventListener('mousedown', func)
    return () => {
      document.removeEventListener('mousedown', func)
    }
  })
  return (
    <div className='likes-page-container'>
      <div className='likes-page-body'>
        {showList.map((painting, index) => {
          return (
            <>
              <div
                className='likes-page-item'
                onClick={() => handleSelectItem(painting.workId, index)}
              >
                <div
                  className={`tri-icon-${index < size / 2 ? 'top' : 'bottom'}`}
                  style={{ display: iconArr[index] ? 'block' : 'none' }}
                ></div>
                <img src={painting.src ? SRC_URL + painting.src : ''} alt='' />
                <div className='item-desc'>
                  <div className='item-pos-box'>
                    <div className='item-name' title={painting.workName}>
                      {painting.workName}
                    </div>
                    <div className='second-line'>
                      <div className='item-museum' title={painting.authorName}>
                        {painting.dynasty}
                        {painting.authorName ? '/' : ''}
                        {painting.authorName}
                      </div>
                      <div className='item-museum' title={painting.museum}>
                        {painting.museum}
                      </div>
                    </div>
                    <div className='icon-box'>
                      <EyeOutlined
                        style={{
                          color: '#fff',
                          fontSize: 16
                        }}
                      />
                      &nbsp;
                      <CloudDownloadOutlined
                        style={{
                          color: '#fff',
                          fontSize: 16
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        })}
        <div
          className='select-work-list'
          id='select-work-list'
          style={{
            display: selectList.length > 1 ? 'flex' : 'none'
          }}
        >
          <SelectWorkList
            showWorkList={selectList}
            listSize={6}
            setComponent={props.setComponent}
            type={1}
          />
        </div>
      </div>
      <div className='likes-page-foot'>
        <div
          className='pre-btn'
          onClick={() => handleChangePage(nowPage - 1)}
          style={{
            cursor: nowPage === 1 ? 'auto' : 'pointer',
            color: nowPage === 1 ? 'gray' : '#9b7a65',
            borderColor: nowPage === 1 ? 'gray' : '#9b7a65'
          }}
        >
          {'< 上一页'}
        </div>
        <div className='page-box'>
          {pageState.map((item, _index) => {
            if (item === 'L') {
              return (
                <div className='ellipsis-btn' onClick={() => handleChangePage(nowPage - 3)}>
                  ···
                </div>
              )
            } else if (item === 'R') {
              return (
                <div className='ellipsis-btn' onClick={() => handleChangePage(nowPage + 3)}>
                  ···
                </div>
              )
            } else {
              return (
                <div
                  className='page-btn'
                  onClick={() => handleChangePage(item)}
                  style={{
                    cursor: nowPage === item ? 'auto' : 'pointer',
                    color: nowPage === item ? 'white' : '#9b7a65',
                    backgroundColor: nowPage === item ? '#9b7a65' : 'white'
                  }}
                >
                  {item}
                </div>
              )
            }
          })}
        </div>
        <div
          className='pre-btn'
          onClick={() => handleChangePage(nowPage + 1)}
          style={{
            cursor: nowPage === pageTotal ? 'auto' : 'pointer',
            color: nowPage === pageTotal ? 'gray' : '#9b7a65',
            borderColor: nowPage === pageTotal ? 'gray' : '#9b7a65'
          }}
        >
          {'下一页 >'}
        </div>
      </div>
    </div>
  )
}
